// src/admin/pages/News.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    LinearProgress,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

import { apiFetch } from '../../utils/api';

const NEWS_ENDPOINT = '/api/admin/news';

function formatDate(value) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
        // fallback if it's already "YYYY-MM-DD"
        return String(value);
    }
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
}

const emptyForm = {
    title: '',
    eventDate: '',
    summary: '',
    body: '',
};

export default function AdminNewsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Success banner at top
    const [successMessage, setSuccessMessage] = useState('');

    // Dialog state (create + edit)
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
    const [activeId, setActiveId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [dialogError, setDialogError] = useState('');
    const [saving, setSaving] = useState(false);

    // Photo state
    const [existingPhotos, setExistingPhotos] = useState([]); // from DB
    const [removedPhotoIds, setRemovedPhotoIds] = useState([]); // ids to delete on save
    const [newPhotos, setNewPhotos] = useState([]); // { file, previewUrl }

    // Delete-confirm dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // row being deleted
    const [deleteLoading, setDeleteLoading] = useState(false);

    const resetPhotoState = useCallback(() => {
        setExistingPhotos([]);
        setRemovedPhotoIds([]);
        setNewPhotos((prev) => {
            prev.forEach((p) => {
                if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
            });
            return [];
        });
    }, []);

    const loadNews = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiFetch(NEWS_ENDPOINT, {
                credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to load news.');
            }
            setRows(Array.isArray(data.rows) ? data.rows : []);
        } catch (err) {
            console.error('Error loading news:', err);
            setError(err.message || 'Failed to load news.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNews();
    }, [loadNews]);

    const handleOpenCreate = () => {
        resetPhotoState();
        setDialogMode('create');
        setActiveId(null);
        setForm({
            ...emptyForm,
            eventDate: new Date().toISOString().slice(0, 10),
        });
        setDialogError('');
        setDialogOpen(true);
    };

    const handleOpenEdit = async (id) => {
        resetPhotoState();
        setDialogMode('edit');
        setActiveId(id);
        setDialogError('');
        setDialogOpen(true);
        setSaving(true);

        try {
            const res = await apiFetch(`${NEWS_ENDPOINT}/${id}`, {
                credentials: 'include',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to load news post.');
            }

            const post = data.post || {};
            const dateStr = (post.event_date || post.published_at || '').slice(
                0,
                10
            );

            setForm({
                title: post.title || '',
                eventDate: dateStr || '',
                summary: post.summary || '',
                body: post.body || '',
            });

            const photos = Array.isArray(data.photos) ? data.photos : [];
            setExistingPhotos(photos);
        } catch (err) {
            console.error('Error loading news post:', err);
            setDialogError(err.message || 'Failed to load news post.');
        } finally {
            setSaving(false);
        }
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setDialogError('');
        setForm(emptyForm);
        setActiveId(null);
        setDialogMode('create');
        resetPhotoState();
    };

    const handleFormChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleFileChange = (e) => {
        const list = Array.from(e.target.files || []);
        if (!list.length) return;

        setNewPhotos((prev) => [
            ...prev,
            ...list.map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);

        // allow selecting the same file again later if needed
        e.target.value = '';
    };

    const handleRemoveExistingPhoto = (photoId) => {
        setRemovedPhotoIds((prev) =>
            prev.includes(photoId) ? prev : [...prev, photoId]
        );
    };

    const handleRemoveNewPhoto = (index) => {
        setNewPhotos((prev) => {
            const next = [...prev];
            const [removed] = next.splice(index, 1);
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return next;
        });
    };

    const openDeleteDialog = (row) => {
        setDeleteTarget(row);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleteLoading) return;
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        const title = deleteTarget.title || 'news post';

        try {
            setDeleteLoading(true);
            const res = await apiFetch(
                `${NEWS_ENDPOINT}/${deleteTarget.id}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to delete news post.');
            }

            await loadNews();
            setSuccessMessage(`Deleted "${title}".`);
            setError('');
            closeDeleteDialog();
        } catch (err) {
            console.error('Delete news error:', err);
            setError(err.message || 'Failed to delete news post.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDialogSubmit = async (e) => {
        e.preventDefault();
        setDialogError('');

        if (!form.title.trim()) {
            setDialogError('Title is required.');
            return;
        }
        if (!form.eventDate) {
            setDialogError('Event date is required.');
            return;
        }

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('title', form.title.trim());
            fd.append('event_date', form.eventDate);
            if (form.summary) fd.append('summary', form.summary);
            if (form.body) fd.append('body', form.body);

            newPhotos.forEach((p) => {
                fd.append('images', p.file);
            });

            if (dialogMode === 'edit' && removedPhotoIds.length) {
                fd.append(
                    'remove_photo_ids',
                    JSON.stringify(removedPhotoIds)
                );
            }

            const url =
                dialogMode === 'create'
                    ? NEWS_ENDPOINT
                    : `${NEWS_ENDPOINT}/${activeId}`;
            const method = dialogMode === 'create' ? 'POST' : 'PUT';

            const res = await apiFetch(url, {
                method,
                credentials: 'include',
                body: fd,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Failed to save news post.');
            }

            setDialogOpen(false);
            setForm(emptyForm);
            resetPhotoState();
            setActiveId(null);
            setDialogMode('create');

            await loadNews();
            setError('');
            setSuccessMessage(
                dialogMode === 'create'
                    ? 'News post created successfully.'
                    : 'News post updated successfully.'
            );
        } catch (err) {
            console.error('Save news error:', err);
            setDialogError(err.message || 'Failed to save news post.');
        } finally {
            setSaving(false);
        }
    };

    const photosCount = (row) => {
        if (typeof row.photos_count === 'number') return row.photos_count;
        if (Array.isArray(row.photos)) return row.photos.length;
        return 0;
    };

    const visibleExistingPhotos = existingPhotos.filter(
        (p) => !removedPhotoIds.includes(p.id)
    );

    return (
        <Box>
            {/* Success banner */}
            {successMessage && (
                <Box sx={{ mb: 2 }}>
                    <Alert
                        severity="success"
                        variant="outlined"
                        onClose={() => setSuccessMessage('')}
                    >
                        {successMessage}
                    </Alert>
                </Box>
            )}

            {/* Header row */}
            <Box
                sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        News
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create and manage News &amp; Updates posts shown on the
                        public site.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                >
                    Create news post
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {loading && <LinearProgress />}
                <TableContainer sx={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small" aria-label="News posts table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 160 }}>
                                    Event date
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        width: 120,
                                        textAlign: 'center',
                                    }}
                                >
                                    Photos
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        width: 180,
                                        textAlign: 'right',
                                    }}
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ py: 2 }}
                                        >
                                            No news posts yet. Click
                                            &ldquo;Create news post&rdquo; to
                                            add one.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {rows.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {row.title}
                                        </Typography>
                                        {row.summary && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.25,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {row.summary}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(row.event_date)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <ImageIcon fontSize="small" />
                                            <Typography variant="body2">
                                                {photosCount(row)}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            justifyContent="flex-end"
                                        >
                                            <Button
                                                size="small"
                                                variant="text"
                                                startIcon={
                                                    <EditIcon fontSize="small" />
                                                }
                                                onClick={() =>
                                                    handleOpenEdit(row.id)
                                                }
                                            >
                                                Edit
                                            </Button>
                                            <Tooltip title="Delete post">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        openDeleteDialog(row)
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {error && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Create / Edit dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {dialogMode === 'create'
                        ? 'Create news post'
                        : 'Edit news post'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleDialogSubmit}
                        sx={{ mt: 1.5 }}
                    >
                        <Stack spacing={2}>
                            <TextField
                                label="Title"
                                value={form.title}
                                onChange={handleFormChange('title')}
                                fullWidth
                                required
                            />

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                            >
                                <TextField
                                    label="Event date"
                                    type="date"
                                    value={form.eventDate}
                                    onChange={handleFormChange('eventDate')}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200 }}
                                    required
                                />
                            </Stack>

                            <TextField
                                label="Summary (short teaser)"
                                value={form.summary}
                                onChange={handleFormChange('summary')}
                                fullWidth
                                multiline
                                minRows={2}
                            />

                            <TextField
                                label="Full story / body"
                                value={form.body}
                                onChange={handleFormChange('body')}
                                fullWidth
                                multiline
                                minRows={4}
                            />

                            {/* Photos */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 0.5, fontWeight: 600 }}
                                >
                                    Photos
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <Button variant="outlined" component="label">
                                        Upload photos
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            hidden
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        The first photo is used as the cover
                                        image on the public site.
                                    </Typography>
                                </Stack>

                                <Box
                                    sx={(theme) => ({
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        p: 1,
                                        minHeight: 80,
                                        maxHeight: 200,
                                        display: 'flex',
                                        gap: 1,
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        alignItems: 'center',
                                        backgroundColor:
                                        theme.palette.background.default,
                                    })}
                                >
                                    {visibleExistingPhotos.map((p) => (
                                        <Box
                                            key={`existing-${p.id}`}
                                            sx={{
                                                position: 'relative',
                                                width: 140,
                                                height: 90,
                                                flex: '0 0 auto',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                bgcolor: 'grey.100',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleRemoveExistingPhoto(
                                                        p.id
                                                    )
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor:
                                                        'rgba(0,0,0,0.65)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.85)',
                                                    },
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                            <Box
                                                component="img"
                                                src={p.url || p.gcs_path}
                                                alt={
                                                    p.caption ||
                                                    form.title ||
                                                    'News photo'
                                                }
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    ))}

                                    {newPhotos.map((p, idx) => (
                                        <Box
                                            key={`new-${idx}`}
                                            sx={{
                                                position: 'relative',
                                                width: 140,
                                                height: 90,
                                                flex: '0 0 auto',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                bgcolor: 'grey.100',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleRemoveNewPhoto(idx)
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor:
                                                        'rgba(0,0,0,0.65)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.85)',
                                                    },
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                            <Box
                                                component="img"
                                                src={p.previewUrl}
                                                alt={`New photo ${idx + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>
                                    ))}

                                    {visibleExistingPhotos.length === 0 &&
                                        newPhotos.length === 0 && (
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ px: 1 }}
                                            >
                                                No photos yet. Upload images to
                                                attach them to this story.
                                            </Typography>
                                        )}
                                </Box>
                            </Box>

                            {dialogError && (
                                <Typography variant="body2" color="error">
                                    {dialogError}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDialogSubmit}
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={closeDeleteDialog}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete news post</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        Are you sure you want to delete&nbsp;
                        <strong>
                            {deleteTarget?.title || 'this news post'}
                        </strong>
                        ? This will remove the story and all of its photos.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirmed}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
